<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { number, object, string } from 'yup';

const { documentGroup, nextPosition } = defineProps({
    documentGroup: {
        type: Object,
        required: false,
    },
    nextPosition: {
        type: Number,
        required: true,
    },
});

const { route } = useRoute();

const form = reactive({
    name: documentGroup ? documentGroup.name : '',
    position: documentGroup ? documentGroup.position : nextPosition,
});

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    position: number()
        .typeError('Veuillez saisir un nombre entier.')
        .integer('Veuillez saisir un nombre entier.')
        .min(0, 'La position ne peut pas être négative.')
        .required('Ce champ est obligatoire.'),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.postDocumentGroup', {
                ...(documentGroup ? { documentGroup: documentGroup.id } : {}),
            }),
            {
                name: form.name,
                position: Number(form.position),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.docAndFaq', {
                    tab: 'documentGroups',
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />

                    <InputField
                        v-model="form.position"
                        label="Position"
                        legend="Détermine l'ordre d'affichage des groupes dans la liste des documents."
                        field-name="position"
                        field-id="position"
                        type="number"
                        step="1"
                        min="0"
                        required
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="documentGroup"
                    :url="
                        route('front.projectManagement.deleteDocumentGroup', {
                            documentGroup: documentGroup.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.docAndFaq', {
                                    tab: 'documentGroups',
                                }),
                            );
                        }
                    "
                    content="Êtes-vous sûr de vouloir supprimer ce groupe ? Il ne doit être rattaché à aucun document."
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
